import type { Configuration, BackupPostureControl } from '@repo/api-gw-sdk';
import {
  BackupPostureControlSortField,
  BackupPostureControlsApi,
  SortOrder,
} from '@repo/api-gw-sdk';
import type { ColumnSort } from '@tanstack/react-table';

import { encodePageToken } from '@/utils/string';

import type { HttpClient } from './httpClient';

const baseUrl = '/backup-posture-controls';

const sortFieldByColumnId: Record<string, BackupPostureControlSortField> = {
  name: BackupPostureControlSortField.CONTROL_SORT_FIELD_NAME,
  severity: BackupPostureControlSortField.CONTROL_SORT_FIELD_SEVERITY,
};

export const controls = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const backupPostureControlsApi = new BackupPostureControlsApi(config);

  return {
    useList: ({
      pageIndex,
      pageSize,
      sorting,
    }: {
      pageIndex?: number;
      pageSize?: number;
      sorting?: ColumnSort;
    } = {}) => {
      const pageToken =
        typeof pageIndex === 'number' && pageIndex > 0
          ? encodePageToken(pageIndex)
          : undefined;
      const sortField = sorting && sortFieldByColumnId[sorting.id];
      const body = sortField
        ? {
            sorts: [
              {
                field: sortField,
                order: sorting.desc ? SortOrder.Desc : SortOrder.Asc,
              },
            ],
          }
        : undefined;

      return httpClient.execute(
        backupPostureControlsApi.listBackupPostureControls.bind(
          backupPostureControlsApi
        ),
        baseUrl,
        '',
        currentProjectId,
        pageToken,
        pageSize,
        body
      );
    },
    useHasControls: () => {
      const { data, ...rest } = httpClient.execute(
        backupPostureControlsApi.listBackupPostureControls.bind(
          backupPostureControlsApi
        ),
        baseUrl,
        'hasControls',
        currentProjectId,
        undefined,
        1
      );
      return { ...rest, data: (data?.backupPostureControls?.length ?? 0) > 0 };
    },
    create: (control: BackupPostureControl) =>
      backupPostureControlsApi.createBackupPostureControl(
        currentProjectId,
        control
      ),
    update: (control: BackupPostureControl) =>
      backupPostureControlsApi.updateBackupPostureControl(
        currentProjectId,
        control.id,
        control
      ),
    delete: (controlId: string) =>
      backupPostureControlsApi.deleteBackupPostureControl(
        currentProjectId,
        controlId
      ),
  };
};
